<template>
    <div class="table">
        <div class="table-header">
            <h4 class="table-title text-center mt-3">{{title}}</h4>
        </div>
        <div class="text-left">
            <div id="search-input-container">
                <label v-if="search" autocomplete="off">
                    <input
                    type="search"
                    class="form-control mb-2"
                    autocomplete="false"
                    name="search"
                    :placeholder="$t('templates.search')"
                    :disabled="$store.state.ajax_in_progress"
                    v-model="searchInput"
                    @input="searchInputFunction"
                >
                </label>

                <div v-if="exportable" class="actions float-right mb-3" v-b-tooltip.hover style="display: inline-block;" :title="$t('misc.export_excel')">
                    <a href="#" class="btn btn-info text-white" @click.prevent="exportExcel()">
                        <i class="fa fa-2x fa-file-excel-o"></i>
                    </a>
                </div>

                <div v-if="scrollBottom" class="actions float-right pr-2 mb-3">
                    <b-btn class="btn btn-dark" :title="$t('misc.scroll_bottom')" @click="scrollBottomAction()">
                        <i class="fa fa-2x fa-level-down" aria-hidden="true"></i>
                    </b-btn>
                </div>
            </div>
        </div>

        <div class="table-responsive" id="dataTable">
            <table ref="table" class="table table-bordered table-hover">
                <thead>
                    <template v-if="topTableHeader">
                        <tr v-if="typeof topTableHeaderData.values != 'undefined'">
                            <td :colspan="(topTableHeaderData.start+1)" align="right" style="visibility: hidden;"></td>
                            <th v-for="(value, text) in topTableHeaderData.values" :colspan="value.colspan" align="center" class="text-center" :class="typeof value.class != 'undefined' ? value.class : ''">
                                {{$t(value.text)}}
                            </th>
                        </tr>
                    </template>

                    <template v-if="tableHeader">
                        <tr v-if="typeof tableHeaderData.values != 'undefined'">
                            <td :colspan="(tableHeaderData.start+1)" align="right" style="visibility: hidden;"></td>
                            <td v-for="(value, text) in tableHeaderData.values" :colspan="value.colspan" align="center" class="text-center" :class="typeof value.class != 'undefined' ? value.class : ''">
                                {{$t(value.text)}}
                            </td>
                        </tr>
                    </template>

                    <template v-if="grandTotal">
                        <tr v-if="grandTotalData.values" v-for="(values, text) in this.grandTotalData.values">
                            <td :colspan="(grandTotalData.start+1)" align="right">{{$t(text)}}</td>
                            <td v-for="value in values">
                                <span style="display:none">{{value = value +""}}</span>
                                <span v-if="value.indexOf('.') > -1">{{value | currency}}</span>
                                <span v-else>{{value}}</span>
                            </td>
                        </tr>
                        <tr v-if="grandTotalData.header">
                            <td :colspan="(grandTotalData.start+1)"></td>
                            <td v-for="value in this.grandTotalData.header" :colspan="value.colspan" align="center" style="border-bottom:2px solid black;border-right:2px solid black;">
                                {{$t(value.text)}}
                            </td>
                        </tr>
                    </template>
                    <tr>
                        <th nowrap v-for="(column, index) in columns" @click="sort(index, column)" :class="(column.sortable !== false  ? 'sortable' : '' )
                            + (sortColumn === index ?
                                (sortType === 'desc' ? ' sorting-desc' : ' sorting-asc') : '')" :style="{width: column.width ? column.width : 'auto'}" :key="index">
                            {{ column.label}}
                            <i class="fa float-right" :class="(sortColumn === index ?
                                (sortType === 'desc' ? ' fa fa-angle-down' : ' fa fa-angle-up') : '')"></i>
                        </th>
                        <slot name="thead-tr"></slot>
                    </tr>
                </thead>
                <tbody>
                    <tr v-for="(row, index2) in paginated" @click="click(row, index2)" :key="index2">
                        <template v-for="(column,index) in columns">
                            <!-- Numeric column -->
                            <td :class="column.numeric ? 'numeric' : ''" v-if="column.html" v-html="collect(row, column.field, column)" :key="index">
                                <a href="#" v-if="column.clickable" @click.prevent="clickSpecificRow(row, index, column)">{{ collect(row, column.field, column) }}
                                </a>
                            </td>

                            <!-- Inputs -->
                            <td v-else-if="column.showInput || column.showTextInput || column.showSelect || column.showDateInput || column.showTextAreaInput || column.showCheckBox || column.showBoolean || column.showBCheckBox" :key="index">
                                <template v-if="column.showInput">
                                    <input type="text" maxlength="10" size="2" class="form-control m-1" :value="collect(row, column.field, column)" @keyup="enterInput(row, $event)" :style="collect(row, column.customStyle, column)">
                                </template>

                                <template v-if="column.showTextInput">
                                    <input
                                        class="form-control"
                                        type="text"
                                        maxlength="50"
                                        :disabled="$store.state.ajax_in_progress || disabledInputs"
                                        @change="changeTextInput(row, index, column, column.field, row[column.field])"
                                        v-model="row[column.field]"
                                    >
                                </template>

                                <template v-if="column.showSelect">
                                    <select
                                        class="form-control"
                                        style="min-width: 150px;"
                                        :disabled="$store.state.ajax_in_progress || disabledInputs"
                                        @change="changeSelect(row, index, column, column.field, row[column.field])"
                                        v-model="row[column.field]"
                                    >
                                        <option v-for="option in rowChangeSelectOptions[column.field]" :value="option.id" :key="option.id">
                                            {{option.name}}
                                        </option>
                                    </select>
                                </template>

                                <template v-if="column.showDateInput">
                                    <flat-pickr
                                        class="form-control bg-white"
                                        :config="{enableTime: false, inline: false}"
                                        :disabled="$store.state.ajax_in_progress || disabledInputs"
                                        @input="changeDate(row, index, column, column.field, row[column.field])"
                                        v-model="row[column.field]"
                                    >
                                    </flat-pickr>
                                </template>

                                <template v-if="column.showTextAreaInput">
                                    <textarea
                                        class="form-control bg-white"
                                        maxlength="5000"
                                        :disabled="$store.state.ajax_in_progress || disabledInputs"
                                        @change="changeTextAreaInput(row, index, column, column.field, row[column.field])"
                                        v-model="row[column.field]"
                                    >
                                    </textarea>
                                </template>

                                <template v-if="column.showBoolean">
                                   <toggle-button
                                        :sync="true"
                                        :value="false"
                                        :disabled="$store.state.ajax_in_progress || disabledInputs"
                                        :labels="{checked: $t('misc.yes'), unchecked: $t('misc.no')}"
                                        @change="changeBooleanInput(row, index, column, column.field, row[column.field])"
                                        v-model="row[column.field]"
                                    />
                                </template>

                                <template v-if="column.showBCheckBox">
                                    <b-form-checkbox
                                        size="lg"
                                        @change="changeBCheckBox(...arguments, row, index, column, column.field)"
                                        v-model="row[column.field]"
                                    >
                                    </b-form-checkbox>
                                </template>

                                <template v-if="showCheckBox(row, column)">
                                    <span v-b-tooltip.hover style="display: inline-block;" :title="$t('general.buttons.change_value')" class="text-center">
                                        <input v-if="showCheckBoxCheckedModel" :value="row.id" v-model="showCheckBoxCheckedModel" type="checkbox" class="checkboxes m-1 sortable" @click="checkBox(row, index)">
                                        <input v-else type="checkbox" class="m-1 sortable" @click="checkBox(row, index)">
                                    </span>
                                </template>
                            </td>

                            <!-- Buttons -->
                            <td v-else-if="column.showHourGlassButton || column.showPartsButton || column.showCloudDownloadButton || column.showPartStockButton || column.showViewButton || column.showCalendarOButton || column.showHistoryButton || column.showCloneButton || column.showPlusButton || column.showDeleteButton || column.showEditButton || column.showPermissionsButton || column.showRolesButton || column.showPricelistButton || column.showStepsButton || column.showTaskButton || column.showTeamMembersButton || column.showPickup || column.showPDF || column.showTeamsButton || column.showPDFBlue || column.showItemButton || column.showRedirectButton || column.showOtherItemsButton || column.showStepsButtonPiping || column.showStepsButtonEquipment || column.showStepsButtonEI || column.showStepsButtonSteelStructure || column.showStepsButtonCivil || column.showStepsButtonPainting || column.showStepsButtonInsulation || column.showFileButton || column.showAddFilesButton" :key="index">
                                <span v-if="showEditButton(row, column)" v-b-tooltip.hover style="display: inline-block;" :title="$t('general.buttons.edit')">
                                    <template>
                                        <i class='fa fa-2x fa-pencil text-success m-1 sortable' @click="editRow(row, index)"></i>
                                    </template>
                                </span>

                                <span v-if="showDeleteButton(row, column)" v-b-tooltip.hover style="display: inline-block;" :title="$t('general.buttons.delete')">
                                    <template>
                                        <i class='fa fa-2x fa-trash text-danger m-1 sortable' @click="deleteRow(row, index)"></i>
                                    </template>
                                </span>

                                <span v-if="showRevisionButton(row, column)" v-b-tooltip.hover style="display: inline-block;" :title="$t('general.buttons.revision')">
                                    <template>
                                        <i class='fa fa-2x fa-list-alt text-info m-1 sortable' @click="revisionRow(row, index)"></i>
                                    </template>
                                </span>

                                <span v-if="showViewButton(row, column)" v-b-tooltip.hover style="display: inline-block;" :title="$t('general.buttons.view')">
                                    <template>
                                        <i class='fa fa-2x fa-eye text-default m-1 sortable' @click="viewRow(row, index)"></i>
                                    </template>
                                </span>

                                <span v-if="showPlusButton(row, column)" v-b-tooltip.hover style="display: inline-block;" :title="$t('general.buttons.duplicate')">
                                    <template>
                                        <i class='fa fa-2x fa-plus text-success m-1 sortable' @click="duplicateRow(row, index)"></i>
                                    </template>
                                </span>

                                <span v-if="showCalendarOButton(row, column)" v-b-tooltip.hover style="display: inline-block;" :title="$t('general.buttons.hour_recording')">
                                    <template>
                                        <i class='fa fa-2x fa-calendar-o text-success m-1 sortable' @click="calendarORow(row, index)"></i>
                                    </template>
                                </span>

                                <span v-if="showCloneButton(row, column)" v-b-tooltip.hover style="display: inline-block;" :title="$t('general.buttons.clone')">
                                    <template>
                                        <i class='fa fa-2x fa-clone text-default m-1 sortable' @click="cloneRow(row, index)"></i>
                                    </template>
                                </span>

                                <span v-if="showHourGlassButton(row, column)" v-b-tooltip.hover style="display: inline-block;" :title="$t('general.buttons.milestones')">
                                    <template>
                                        <i class="fa fa-2x fa-hourglass-end text-default m-1 sortable" @click="hourGlassRow(row, index)"></i>
                                    </template>
                                </span>

                                <span v-if="showItemsButton(row, column)" v-b-tooltip.hover style="display: inline-block;" :title="$t('general.buttons.items')">
                                    <template>
                                        <i class="fa fa-2x fa-align-center text-default m-1 sortable" @click="partsRow(row, index)"></i>
                                    </template>
                                </span>

                                <span v-if="showPartsButton(row, column)" v-b-tooltip.hover style="display: inline-block;" :title="$t('general.buttons.parts')">
                                    <template>
                                        <i class="fa fa-2x fa-th-list text-default m-1 sortable" @click="partsRow(row, index)"></i>
                                    </template>
                                </span>

                                <span v-if="showPartStockButton(row, column)" v-b-tooltip.hover style="display: inline-block;" :title="$t('general.buttons.part_stock')">
                                    <template>
                                        <i class="fa fa-2x fa-th-list text-default m-1 sortable" @click="partsRow(row, index)"></i>
                                    </template>
                                </span>

                                <span v-if="showOtherItemsButton(row, column)" v-b-tooltip.hover style="display: inline-block;" :title="$t(otherItemsTitle)">
                                    <template>
                                        <i class="fa fa-2x fa-bars text-secondary m-1 sortable" @click="otherItems(row, index)"></i>
                                    </template>
                                </span>

                                <span v-if="showHistoryButton(row, column)" v-b-tooltip.hover style="display: inline-block;" :title="$t(historyButtonTitle)">
                                    <template>
                                        <i class='fa fa-2x fa fa-table text-info m-1 sortable' @click="historyRow(row, index)"></i>
                                    </template>
                                </span>

                                <span v-if="showCloudDownloadButton(row, column)" v-b-tooltip.hover style="display: inline-block;" :title="$t(cloudDownloadButtonTitle)">
                                    <template>
                                        <i class="fa fa-2x fa-cloud-download text-primary m-1 sortable" @click="cloudDownloadRow(row, index)"></i>
                                    </template>
                                </span>

                                <span v-if="showTaskButton(row, column)" v-b-tooltip.hover style="display: inline-block;" :title="$t('general.buttons.tasks')">
                                    <template>
                                        <i class='fa fa-2x fa-thumb-tack text-dark m-1 sortable' @click="taskRow(row, index)"></i>
                                    </template>
                                </span>

                                <span v-if="showPermissionsButton(row, column)" v-b-tooltip.hover style="display: inline-block;" :title="$t('general.buttons.permissions')">
                                    <template>
                                        <i class='fa fa-2x fa-lock text-danger m-1 sortable' @click="permissionRow(row, index)"></i>
                                    </template>
                                </span>

                                <span v-if="showTeamMembersButton(row, column)" v-b-tooltip.hover style="display: inline-block;" :title="$t('general.buttons.team_members')">
                                    <template>
                                        <i class='fa fa-2x fa-users text-info m-1 sortable' @click="memberRow(row, index)"></i>
                                    </template>
                                </span>

                                <span v-if="showRolesButton(row, column)" v-b-tooltip.hover style="display: inline-block;" :title="$t('general.buttons.roles')">
                                    <template>
                                        <i class='fa fa-2x fa-user text-info m-1 sortable' @click="roleRow(row, index)"></i>
                                    </template>
                                </span>

                                <span v-if="showCertificatesButton(row, column)" v-b-tooltip.hover style="display: inline-block;" :title="$t('general.buttons.certificates')">
                                    <template>
                                        <i class='fa fa-2x fa-graduation-cap text-dark m-1 sortable' @click="certificateRow(row, index)"></i>
                                    </template>
                                </span>

                                <span v-if="showPricelistButton(row, column)" v-b-tooltip.hover style="display: inline-block;" :title="$t('general.buttons.price_list')">
                                    <template>
                                        <i class='fa fa-2x fa-money-bill text-info m-1 sortable' style="color:green !important" @click="priceRow(row, index)"></i>
                                    </template>
                                </span>

                                <span v-if="showItemButton(row, column)" v-b-tooltip.hover style="display: inline-block;" :title="$t('general.buttons.item')">
                                    <template>
                                        <i class='fa fa-2x fa-align-center text-dark m-1 sortable' @click="itemRow(row, index)"></i>
                                    </template>
                                </span>

                                <span v-if="showRedirectButton(row, column)" v-b-tooltip.hover style="display: inline-block;" :title="$t('general.buttons.redirect')">
                                    <template>
                                        <i class='fa fa-2x fa-share-square-o text-dark m-1 sortable' @click="redirectRow(row, index)"></i>
                                    </template>
                                </span>

                                <span v-if="showRequestButton(row, column)" v-b-tooltip.hover style="display: inline-block;" :title="$t('general.buttons.request')">
                                    <template>
                                        <i class='fa fa-2x fa-clipboard text-secondary m-1 sortable' @click="requestRow(row, index)"></i>
                                    </template>
                                </span>

                                <span v-if="showPickup(row, column)" v-b-tooltip.hover style="display: inline-block;" :title="$t('general.buttons.pickup')">
                                    <template>
                                        <i class='fa fa-2x fa-arrow-circle-up blue--text m-1 sortable' @click="viewPickup(row, index)"></i>
                                    </template>
                                </span>

                                <span v-if="showStepsButton(row, column)" v-b-tooltip.hover style="display: inline-block;" :title="$t('general.buttons.steps')">
                                    <template>
                                        <i class='fa fa-2x fa-list-ol blue--text m-1 sortable' @click="viewSteps(row, index)"></i>
                                    </template>
                                </span>

                                <span v-if="showStepsButtonPiping(row, column)" v-b-tooltip.hover style="display: inline-block;" :title="$t('general.buttons.steps_piping')">
                                    <template>
                                        <i class='fa fa-2x fa-list-ol blue--text m-1 sortable' @click="viewStepsPiping(row, index)"></i>
                                    </template>
                                </span>

                                <span v-if="showStepsButtonEquipment(row, column)" v-b-tooltip.hover style="display: inline-block;" :title="$t('general.buttons.steps_equipment')">
                                    <template>
                                        <i class='fa fa-2x fa-list-ol blue--text m-1 sortable' @click="viewStepsEquipment(row, index)"></i>
                                    </template>
                                </span>

                                <span v-if="showStepsButtonEI(row, column)" v-b-tooltip.hover style="display: inline-block;" :title="$t('general.buttons.steps_ei')">
                                    <template>
                                        <i class='fa fa-2x fa-list-ol blue--text m-1 sortable' @click="viewStepsEI(row, index)"></i>
                                    </template>
                                </span>

                                <span v-if="showStepsButtonSteelStructure(row, column)" v-b-tooltip.hover style="display: inline-block;" :title="$t('general.buttons.steps_steelstructure')">
                                    <template>
                                        <i class='fa fa-2x fa-list-ol blue--text m-1 sortable' @click="viewStepsSteelStructure(row, index)"></i>
                                    </template>
                                </span>

                                <span v-if="showStepsButtonCivil(row, column)" v-b-tooltip.hover style="display: inline-block;" :title="$t('general.buttons.steps_civil')">
                                    <template>
                                        <i class='fa fa-2x fa-list-ol blue--text m-1 sortable' @click="viewStepsCivil(row, index)"></i>
                                    </template>
                                </span>

                                <span v-if="showStepsButtonPainting(row, column)" v-b-tooltip.hover style="display: inline-block;" :title="$t('general.buttons.steps_painting')">
                                    <template>
                                        <i class='fa fa-2x fa-list-ol blue--text m-1 sortable' @click="viewStepsPainting(row, index)"></i>
                                    </template>
                                </span>

                                <span v-if="showStepsButtonInsulation(row, column)" v-b-tooltip.hover style="display: inline-block;" :title="$t('general.buttons.steps_insulation')">
                                    <template>
                                        <i class='fa fa-2x fa-list-ol blue--text m-1 sortable' @click="viewStepsInsulation(row, index)"></i>
                                    </template>
                                </span>

                                <span v-if="showPDF(row, column)" v-b-tooltip.hover style="display: inline-block;" :title="$t(downloadPDF)">
                                    <template>
                                        <i class='fa fa-2x fa-file-pdf-o text-default m-1 sortable' @click="viewPDF(row, index)"></i>
                                    </template>
                                </span>

                                <span v-if="showPDFBlue(row, column)" v-b-tooltip.hover style="display: inline-block;" :title="$t(downloadPDFBlue)">
                                    <template>
                                        <i class='fa fa-2x fa-file-pdf-o text-primary m-1 sortable' @click="viewPDFBlue(row, index)"></i>
                                    </template>
                                </span>

                                <span v-if="showFileButton(row, column)" v-b-tooltip.hover style="display: inline-block;" :title="$t('general.buttons.show_file')">
                                    <template>
                                        <i class='fa fa-2x fa-file m-1 sortable' @click="viewFile(row, index)"></i>
                                    </template>
                                </span>

                                <span v-if="showAddFilesButton(row, column)" v-b-tooltip.hover style="display: inline-block;" :title="$t('general.buttons.add_files')">
                                    <template>
                                        <i class='fa fa-2x fa-file text-info m-1 sortable' @click="addFiles(row, index)"></i>
                                    </template>
                                </span>

                                <span v-if="showTeamsButton(row, column)" v-b-tooltip.hover style="display: inline-block;" :title="$t('general.buttons.show_teams')">
                                    <template>
                                        <i class='fas fa-2x fa-user-friends text-dark m-1 sortable' @click="viewTeams(row, index)"></i>
                                    </template>
                                </span>
                            </td>

                            <td v-else-if="showFile(row, column)" :key="index">
                                <template v-if="column.showFile">
                                    <i class='fa fa-2x fa-file text-default m-1 sortable' @click="fileRow(row, index)"></i>
                                </template>
                            </td>

                            <td v-else-if="showItalicIfNull(row, column)" :class="[row[column.field] == 'NULL' ? 'font-italic font-weight-bold' : '']" :key="index">
                                <template v-if="column.showItalicIfNull">
                                    {{row[column.field]}}
                                </template>
                            </td>

                            <td v-else-if="showAsCheck(row, column)" :key="index" :style="typeof rowsColor != 'undefined'
                            && typeof rowsColor[index2] != 'undefined'
                            && typeof rowsColor[index2][column.field] != 'undefined' ?
                            'background-color:' + rowsColor[index2][column.field] :
                            'background-color:initial'"
                            class="text-center">
                                <div v-if="row[column.field+'_max']"> <!-- for material management -->
                                    <i v-if="row[column.field+'_id'] > 0 && row[column.field+'_status'] != 'delivered'" class="fa fa-times red--text fa-2x cursor-pointer" @click="clickUnCheck(row, index, column.field)"></i>
                                    <i v-if="!row[column.field+'_id'] && row[column.field+'_count'] > 0 && row[column.field+'_max'] > row[column.field]" class="fa fa-check fa-2x green--text cursor-pointer" @click="clickCheck(row, index, column.field)"></i>
                                    <i v-if="row[column.field+'_count'] == 0 && row[column.field+'_max'] > row[column.field] && !row[column.field+'_id']" class="fa fa-2x fa-ban"></i>
                                    <i v-if="row[column.field+'_id'] && row[column.field+'_status'] == 'delivered'" class="fa fa-2x fa-arrow-circle-up blue--text"></i>
                                </div>
                                <div v-else @click="clickCheck(row, index, column.field)" :class="{'cursor-pointer': column.cursor != false}">
                                    <i v-if="row[column.field] == 1 && row[column.field] != null" class="fa fa-2x fa-check" :class="{'green--text': row[column.field] == 1}"></i>
                                    <i v-else class="fa fa-2x fa-times"></i>
                                </div>
                            </td>

                            <td v-else-if="showAsCheckRed(row, column)" :key="index">
                                <div @click="clickCheck(row, index, column.field)" :class="{'cursor-pointer': column.cursor != false}" class="text-center">
                                    <i v-if="row[column.field] == 1 && row[column.field] != null" class="fa fa-2x fa-check" :class="{'red--text': row[column.field] == 1}"></i>
                                    <i v-else class="fa fa-2x fa-times"></i>
                                </div>
                            </td>

                            <td v-else-if="showAsCheckSubcontractors(row, column)" :key="index">
                                <div @click="clickCheckSubcontractors(row, index, column.field)" class="cursor-pointer">
                                    <i v-if="row.company2project[column.field.substring('company2project.'.length)] == 1 && row.company2project[column.field.substring('company2project.'.length)] != null" class="fa fa-2x fa-check" :class="{'green--text': row.company2project[column.field.substring('company2project.'.length)] == 1}"></i>
                                    <i v-else class="fa fa-2x fa-times"></i>
                                </div>
                            </td>

                            <td :class="column.numeric ? 'numeric' : ''" v-else :key="index"
                            :style="typeof rowsColor != 'undefined'
                            && typeof rowsColor[index2] != 'undefined'
                            && typeof rowsColor[index2][column.field] != 'undefined' ?
                            'background-color:' + rowsColor[index2][column.field] :
                            'background-color:initial'">

                                <a href="#" v-if="column.clickable" @click.prevent="clickSpecificRow(row, index, column.field)">{{ collect(row, column.field, column) }}
                                </a>
                                <template v-else>
                                    {{ collect(row, column.field, column) }}
                                </template>
                            </td>
                        </template>
                        <slot name="tbody-tr" :row="row"></slot>
                    </tr>
                </tbody>
            </table>
        </div>

        <!--
        <div class="row">
            <div class="float-left dt-entries">
                {{rowsCount}} entries
            </div>
        </div>
        -->

        <div class="table-footer" v-if="paginate">
            <paginate
                ref="pagination"
                v-model="page"
                :page-count="pageCount"
                :page-range="3"
                :margin-pages="1"
                :click-handler="clickCallback"
                :prev-text="'&laquo;'"
                :next-text="'&raquo;'"
                :container-class="'pagination'"
                :page-class="'page-item'"
                :page-link-class= "'page-link'"
                :next-link-class= "'page-link'"
                :prev-link-class= "'page-link'"
                class="mt-3 float-right"
            >
            </paginate>
        </div>

        <div v-if="scrollTop" class="actions float-right pr-0 mb-3 mt-3">
            <b-btn class="btn btn-dark" :title="$t('misc.scroll_top')" @click="scrollTopAction()">
                <i class="fa fa-2x fa-level-up" aria-hidden="true"></i>
            </b-btn>
        </div>
    </div>
</template>

<script>
import Vue from 'vue';
import flatPickr from 'vue-flatpickr-component';
import 'flatpickr/dist/flatpickr.css';
import _ from 'lodash';
import VuejsPaginate from 'vuejs-paginate';
Vue.component('paginate', VuejsPaginate);

export default {
    name: 'DataTable',
    components: {
        flatPickr,
    },
    props: {
        title: {
            default: ""
        },
        columns: {
            required: true
        },
        rows: {
            required: true
        },
        rowsColor: {
            type: Array,
            required: false,
        },
        perPage: {
            default: 10
        },
        pageCount: {
            default: 1
        },
        rowsCount: {
            default: 0
        },
        sortable: {
            default: true
        },
        search: {
            default: true
        },
        paginate: {
            default: true
        },
        exportable: {
            default: true
        },
        grandTotal: {
            default: false
        },
        grandTotalData: {
            type: Object,
            required: false,
        },
        topTableHeader: {
            default: false
        },
        topTableHeaderData: {
            type: Object,
            default: function () {
                return {}
            },
        },
        tableHeader: {
            default: false
        },
        tableHeaderData: {
            type: Object,
            default: function () {
                return {}
            },
        },
        showCheckBoxCheckedModel: {
            type: Array,
            default () {
                return []
            }
        },
        pagelen: {
            type: Array,
            default () {
                return [5, 10, 20, 50]
            }
        },
        scrollBottom: {
            type: Boolean,
            default: false
        },
        scrollTop: {
            type: Boolean,
            default: false
        },
        downloadPDF: {
            type: String,
            default: 'general.buttons.show_pdf'
        },
        downloadPDFBlue: {
            type: String,
            default: 'general.buttons.show_pdf'
        },
        otherItemsTitle: {
            type: String,
            default: 'general.buttons.other_items'
        },
        historyButtonTitle: {
            type: String,
            default: 'general.buttons.history'
        },
        cloudDownloadButtonTitle: {
            type: String,
            default: 'general.buttons.download_attachments'
        },
        rowChangeSelectOptions: {
            type: Object,
            default () {
                return null;
            }
        },
        disabledInputs: {
            type: Boolean,
            default: false,
        },
    },
    data() {
        return {
            currentPage: 1,
            currentPerPage: this.perPage,
            sortColumn: -1,
            sortType: 'asc',
            searchInput: '',
            lastInput: '',
            page: 1,
            selectValues: [],
        }
    },
    mounted() {
        this.sort(0);
    },
    methods: {
        exportExcel() {
            if (!this.$store.state.ajax_in_progress) {
                this.$emit('exportButtonClick');
            }
        },
        showCheckBox(row, column) {
            if (typeof (row.show_check_box) != 'undefined') {
                return row.show_check_box;
            } else {
                return column.showCheckBox;
            }
        },
        showViewButton(row, column) {
            if (typeof (row.show_view_button) != 'undefined') {
                return row.show_view_button;
            } else {
                return column.showViewButton;
            }
        },
        showPlusButton(row, column) {
            if (typeof (row.show_plus_button) != 'undefined') {
                return row.show_plus_button;
            } else {
                return column.showPlusButton;
            }
        },
        showCalendarOButton(row, column) {
            if (typeof (row.show_calendar_o_button) != 'undefined') {
                return row.show_calendar_o_button;
            } else {
                return column.showCalendarOButton;
            }
        },
        showHistoryButton(row, column) {
            if (typeof (row.show_history_button) != 'undefined') {
                return row.show_history_button;
            } else {
                return column.showHistoryButton;
            }
        },
        showCloneButton(row, column) {
            if (typeof (row.show_clone_button) != 'undefined') {
                return row.show_clone_button;
            } else {
                return column.showCloneButton;
            }
        },
        showItemsButton(row, column) {
            if (typeof (row.show_items_button) != 'undefined') {
                return row.show_items_button;
            } else {
                return column.showItemsButton;
            }
        },
        showPartsButton(row, column) {
            if (typeof (row.show_parts_button) != 'undefined') {
                return row.show_parts_button;
            } else {
                return column.showPartsButton;
            }
        },
        showCloudDownloadButton(row, column) {
            if (typeof (row.show_cloud_download_button) != 'undefined') {
                return row.show_cloud_download_button;
            } else {
                return column.showCloudDownloadButton;
            }
        },
        showItalicIfNull(row, column) {
            if (typeof (row.show_italic_if_null) != 'undefined') {
                return row.show_italic_if_null;
            } else {
                return column.showItalicIfNull;
            }
        },
        showOtherItemsButton(row, column) {
            if (typeof (row.show_other_items_button) != 'undefined') {
                return row.show_other_items_button;
            } else {
                return column.showOtherItemsButton;
            }
        },
        showPartStockButton(row, column) {
            if (typeof (row.show_part_stock_button) != 'undefined') {
                return row.show_part_stock_button;
            } else {
                return column.showPartStockButton;
            }
        },
        showPricelistButton(row, column) {
            if (typeof (row.show_pricelist_button) != 'undefined') {
                return row.show_pricelist_button;
            } else {
                return column.showPricelistButton;
            }
        },
        showRevisionButton(row, column) {
            if (typeof (column.forceShowRevisionButton) != 'undefined') {
                return column.forceShowRevisionButton;
            }

            if (typeof (row.show_revision_button) != 'undefined') {
                return row.show_revision_button;
            } else {
                return column.showRevisionButton;
            }
        },
        showItemButton(row, column) {
            if (typeof (column.forceShowItemButton) != 'undefined') {
                return column.forceShowItemButton;
            }

            if (typeof (row.show_item_button) != 'undefined') {
                return row.show_item_button;
            } else {
                return column.showItemButton;
            }
        },
        showRedirectButton(row, column) {
            if (typeof (row.show_redirect_button) != 'undefined') {
                return row.show_redirect_button;
            } else {
                return column.showRedirectButton;
            }
        },
        showRequestButton(row, column) {
            if (typeof (column.forceShowRequestButton) != 'undefined') {
                return column.forceShowRequestButton;
            }

            if (typeof (row.show_request_button) != 'undefined') {
                return row.show_request_button;
            } else {
                return column.showRequestButton;
            }
        },
        showPDF(row, column) {
            if (typeof (row.show_pdf) != 'undefined') {
                return row.show_pdf;
            } else {
                return column.showPDF;
            }
        },
        showPDFBlue(row, column) {
            if (typeof (row.show_pdf_blue) != 'undefined') {
                return row.show_pdf_blue;
            } else {
                return column.showPDFBlue;
            }
        },
        showPickup(row, column) {
            if (typeof (row.show_pickup) != 'undefined') {
                return row.show_pickup;
            } else {
                return column.showPickup;
            }
        },

        //Steps
        showStepsButton(row, column) {
            if (typeof (row.show_steps_button) != 'undefined') {
                return row.show_steps_button;
            } else {
                return column.showStepsButton;
            }
        },
        showStepsButtonPiping(row, column) {
            if (typeof (row.show_steps_button_piping) != 'undefined') {
                return row.show_steps_button_piping;
            } else {
                return column.showStepsButtonPiping;
            }
        },
        showStepsButtonEquipment(row, column) {
            if (typeof (row.show_steps_button_equipment) != 'undefined') {
                return row.show_steps_button_equipment;
            } else {
                return column.showStepsButtonEquipment;
            }
        },
        showStepsButtonEI(row, column) {
            if (typeof (row.show_steps_button_ei) != 'undefined') {
                return row.show_steps_button_ei;
            } else {
                return column.showStepsButtonEI;
            }
        },
        showStepsButtonSteelStructure(row, column) {
            if (typeof (row.show_steps_button_steelstructure) != 'undefined') {
                return row.show_steps_button_steelstructure;
            } else {
                return column.showStepsButtonSteelStructure;
            }
        },
        showStepsButtonCivil(row, column) {
            if (typeof (row.show_steps_button_civil) != 'undefined') {
                return row.show_steps_button_civil;
            } else {
                return column.showStepsButtonCivil;
            }
        },
        showStepsButtonPainting(row, column) {
            if (typeof (row.show_steps_button_painting) != 'undefined') {
                return row.show_steps_button_painting;
            } else {
                return column.showStepsButtonPainting;
            }
        },
        showStepsButtonInsulation(row, column) {
            if (typeof (row.show_steps_button_insulation) != 'undefined') {
                return row.show_steps_button_insulation;
            } else {
                return column.showStepsButtonInsulation;
            }
        },
        //End of Steps

        showFile(row, column) {
            if (typeof (row.show_file) != 'undefined') {
                return row.show_file;
            } else {
                return column.showFile;
            }
        },
        showFileButton(row, column) {
            if (typeof (row.show_file_button) != 'undefined') {
                return row.show_file_button;
            } else {
                return column.showFileButton;
            }
        },
        showAddFilesButton(row, column) {
            if (typeof (row.show_add_files_button) != 'undefined') {
                return row.show_add_files_button;
            } else {
                return column.showAddFilesButton;
            }
        },
        showAsCheck(row, column) {
            if (typeof (row.show_as_check) != 'undefined') {
                return row.show_as_check;
            } else {
                return column.showAsCheck;
            }
        },
        showAsCheckRed(row, column) {
            if (typeof (row.show_as_check_red) != 'undefined') {
                return row.show_as_check_red;
            } else {
                return column.showAsCheckRed;
            }
        },
        showAsCheckSubcontractors(row, column) {
            if (typeof (row.show_as_check_subcontractors) != 'undefined') {
                return row.show_as_check_subcontractors;
            } else {
                return column.showAsCheckSubcontractors;
            }
        },
        showTeamMembersButton(row, column) {
            if (typeof (row.show_team_members_button) != 'undefined') {
                return row.show_team_members_button;
            } else {
                return column.showTeamMembersButton;
            }
        },
        showRolesButton(row, column) {
            if (typeof (row.show_roles_button) != 'undefined') {
                return row.show_roles_button;
            } else {
                return column.showRolesButton;
            }
        },
        showPermissionsButton(row, column) {
            if (typeof (row.show_permissions_button) != 'undefined') {
                return row.show_permissions_button;
            } else {
                return column.showPermissionsButton;
            }
        },
        showHourGlassButton(row, column) {
            if (typeof (row.show_hour_glass_button) != 'undefined') {
                return row.show_hour_glass_button;
            } else {
                return column.showHourGlassButton;
            }
        },
        showCertificatesButton(row, column) {
            if (typeof (row.show_certificates_button) != 'undefined') {
                return row.show_certificates_button;
            } else {
                return column.showCertificatesButton;
            }
        },
        showTaskButton(row, column) {
            if (typeof (row.show_task_button) != 'undefined') {
                return row.show_task_button;
            } else {
                return column.showTaskButton;
            }
        },
        showTeamsButton(row, column) {
            if (typeof (row.show_teams_button) != 'undefined') {
                return row.show_teams_button;
            } else {
                return column.showTeamsButton;
            }
        },
        showEditButton(row, column) {
            if (typeof (column.forceShowEditButton) != 'undefined') {
                return column.forceShowEditButton;
            }

            if (typeof (row.show_edit_button) != 'undefined') {
                return row.show_edit_button;
            } else {
                return column.showEditButton;
            }
        },
        showDeleteButton(row, column) {
            if (typeof (column.forceShowDeleteButton) != 'undefined') {
                return column.forceShowDeleteButton;
            }

            if (typeof (row.show_delete_button) != 'undefined') {
                return row.show_delete_button;
            } else {
                return column.showDeleteButton;
            }
        },
        clickCallback (page) {
            this.$emit('changePage', page);
        },
        sort(index, column) {
            if (column) {
                this.sortType = this.sortType === 'asc' ? 'desc' : 'asc';
                this.$emit('orderByEvent', column, this.sortType);
            } else {
                return;
            }
        },

        click(row, index) {
            this.$emit("rowClick", row, index);
        },

        clickSpecificRow(row, index, column) {
            this.$emit('clickSpecificRow', row, index, column);
        },

        checkBox (row, index) {
            this.$emit('rowCheckBox', row, index);
        },

        enterInput (row, $event) {
            this.$emit('rowEnterInput', row, $event.target.value);
        },

        viewPDF (row, index) {
            if (!this.$store.state.ajax_in_progress) {
                this.$emit('rowClickPDF', row, index);
            }
        },

        viewPDFBlue (row, index) {
            if (!this.$store.state.ajax_in_progress) {
                this.$emit('rowClickPDFBlue', row, index);
            }
        },

        viewTeams (row, index) {
            this.$emit('rowClickTeams', row, index);
        },

        viewFile (row, index) {
            this.$emit('rowClickFile', row, index);
        },

        addFiles (row, index) {
            this.$emit('rowClickAddFiles', row, index);
        },

        hourGlassRow(row, index) {
            this.$emit('rowClickHourGlass', row, index);
        },

        partsRow(row, index) {
            this.$emit('rowClickParts', row, index);
        },

        cloudDownloadRow(row, index) {
            this.$emit('rowClickCloudDownloadRow', row, index);
        },

        otherItems(row, index) {
            this.$emit('rowClickOtherItems', row, index);
        },

        viewRow(row, index) {
            this.$emit('rowClickView', row, index);
        },

        duplicateRow(row, index) {
            this.$emit('rowDuplicate', row, index);
        },

        calendarORow(row, index) {
            this.$emit('rowCalendarO', row, index);
        },

        historyRow(row, index) {
            this.$emit('rowHistory', row, index);
        },

        cloneRow(row, index) {
            this.$emit('rowCloneRow', row, index);
        },

        viewPickup(row, index) {
            this.$emit('rowClickPickup', row, index);
        },

        //Steps
        viewSteps(row, index) {
            this.$emit('rowClickSteps', row, index);
        },
        viewStepsPiping(row, index) {
            this.$emit('rowClickStepsPiping', row, index);
        },
        viewStepsEquipment(row, index) {
            this.$emit('rowClickStepsEquipment', row, index);
        },
        viewStepsEI(row, index) {
            this.$emit('rowClickStepsEI', row, index);
        },
        viewStepsSteelStructure(row, index) {
            this.$emit('rowClickStepsSteelStructure', row, index);
        },
        viewStepsCivil(row, index) {
            this.$emit('rowClickStepsCivil', row, index);
        },
        viewStepsPainting(row, index) {
            this.$emit('rowClickStepsPainting', row, index);
        },
        viewStepsInsulation(row, index) {
            this.$emit('rowClickStepsInsulation', row, index);
        },
        //End of Steps

        deleteRow(row, index) {
            this.$emit('rowClickDelete', row, index);
        },

        permissionRow(row, index) {
            this.$emit('rowClickPermission', row, index);
        },

        roleRow(row, index) {
            this.$emit('rowClickRole', row, index);
        },

        memberRow(row, index) {
            this.$emit('rowClickTeamMembers', row, index);
        },

        priceRow(row, index) {
            this.$emit('rowClickPricelist', row, index);
        },

        revisionRow(row, index) {
            this.$emit('rowClickRevision', row, index);
        },

        itemRow(row, index) {
            this.$emit('rowClickItem', row, index);
        },

        redirectRow(row, index) {
            this.$emit('rowClickRedirect', row, index);
        },

        requestRow(row, index) {
            this.$emit('rowClickRequests', row, index);
        },

        certificateRow(row, index) {
            this.$emit('rowClickCertificate', row, index);
        },

        fileRow(row, index) {
            this.$emit('rowClickFile', row, index);
        },

        clickCheck(row, index, column) {
            this.$emit('rowClickCheck', row, index, column);
        },
        clickCheckSubcontractors(row, index, column) {
            this.$emit('rowClickCheckSubcontractors', row, index, column);
        },
        clickUnCheck(row, index, column) {
            this.$emit('rowUnClickCheck', row, index, column);
        },

        editRow(row, index) {
            this.$emit('rowClickEdit', row, index);
        },

        taskRow(row, index) {
            this.$emit('rowClickTask', row, index);
        },
        scrollBottomAction () {
            var container = this.$el.querySelector("#dataTable");
            window.scrollTo(0, container.scrollHeight);
        },
        scrollTopAction () {
            var container = this.$el.querySelector("#dataTable");
            window.scrollTo(0, 0);
        },

        changeTextInput(row, index, column, column_name, value) {
            this.$emit('rowChangeTextInput', row, index, column, column_name, value);
        },
        changeSelect(row, index, column, column_name, value) {
            this.$emit('rowChangeSelect', row, index, column, column_name, value);
        },
        changeDate(row, index, column, column_name, value) {
            this.$emit('rowChangeDate', row, index, column, column_name, value);
        },
        changeTextAreaInput(row, index, column, column_name, value) {
            this.$emit('rowChangeTextAreaInput', row, index, column, column_name, value);
        },
        changeBooleanInput(row, index, column, column_name, value) {
            this.$emit('rowChangeBooleanInput', row, index, column, column_name, value);
        },
        changeBCheckBox(value, row, index, column, column_name) {
            this.$emit('rowChangeBCheckBox', value, row, index, column, column_name);
        },

        //Table
        renderTable() {
            var table = '<table><thead>';

            table += '<tr>';
            for (var i = 0; i < this.columns.length; i++) {
                const column = this.columns[i];
                table += '<th>';
                table += column.label;
                table += '</th>';
            }
            table += '</tr>';

            table += '</thead><tbody>';

            for (var i = 0; i < this.rows.length; i++) {
                const row = this.rows[i];
                table += '<tr>';
                for (var j = 0; j < this.columns.length; j++) {
                    const column = this.columns[j];
                    table += '<td>';
                    table += this.collect(row, column.field);
                    table += '</td>';
                }
                table += '</tr>';
            }

            table += '</tbody></table>';

            return inline-block;
        },
        dig(obj, selector, type) {
            var result = obj;
            const splitter = selector.split('.');
            for (let i = 0; i < splitter.length; i++) {
                if (typeof (result) === 'undefined') {
                    return undefined;
                } else {
                    result = result[splitter[i]];
                    if (typeof type.numeric != 'undefined') {
                        if (type.numeric) {
                            result = this.$options.filters.currency(result);
                        }
                    }
                    if (typeof type.datetime != 'undefined') {
                        if (type.datetime) {
                            result = this.$options.filters.formatDateTime(result);
                        }
                    }
                }
            }
            return result;
        },
        collect(obj, field, type) {
            if (typeof (field) === 'function') {
                return field(obj);
            }
            else if (typeof (field) === 'string') {
                return this.dig(obj, field, type);
            }
            else {
                return undefined;
            }
        },
        searchInputFunction() {
            this.onSearchInput(this);
        },
        onSearchInput: _.debounce((vm) => {
            vm.$emit('searchEvent', vm.searchInput);
            vm.currentPage = 1;
            vm.paginated;
            if (typeof vm.$refs.pagination != 'undefined') {
                vm.$refs.pagination.selectFirstPage();
            }
        }, 500),
    },
    computed: {
        processedRows: function () {
            var computedRows = this.rows;

            if (this.sortable !== false) {
                computedRows = computedRows.sort((x, y) => {
                    if (!this.columns[this.sortColumn]) {
                        return 0;
                    }

                    const cook = (x) => {
                        x = this.collect(x, this.columns[this.sortColumn].field);
                        if (typeof (x) === 'string') {
                            x = x.toLowerCase();
                            if (this.columns[this.sortColumn].numeric)
                                x = x.indexOf('.') >= 0 ? parseFloat(x) : parseInt(x);
                        }
                        return x;
                    }

                    x = cook(x);
                    y = cook(y);

                    return (x < y ? -1 : (x > y ? 1 : 0)) * (this.sortType === 'desc' ? -1 : 1);
                })
            }

            if (this.searchInput) {
                if(this.lastInput !== this.searchInput) {
                    this.lastInput = this.searchInput;
                }
            }
            return computedRows;
        },
        paginated: function () {
            var paginatedRows = this.processedRows;
            if (this.paginate && this.currentPerPage != -1) {
                paginatedRows = paginatedRows.slice((this.currentPage - 1) * this.currentPerPage, this.currentPage *
                    this.currentPerPage);
            }
            return paginatedRows;
        }
    },
    watch: {
        currentPerPage() {
            this.currentPage = 1;
            this.paginated;
        },
        // searchInput() {
        //     this.$emit('searchEvent', this.searchInput);
        //     this.currentPage = 1;
        //     this.paginated;
        //     if (typeof this.$refs.pagination != 'undefined') {
        //         this.$refs.pagination.selectFirstPage()
        //     }
        // }
    },
}
</script>

<style scoped>
    .table {
        margin-bottom: 0 !important;
    }

    .sortable {
        cursor: pointer;
    }

    .pagination1 {
        color: black;
        float: left;
        padding: 8px 16px;
        text-decoration: none;
    }

    .pagination1 li {
        color: black;
        float: left;
        padding: 8px 16px;
        text-decoration: none;
    }

    td {
        max-width: 500px;
        overflow: hidden;
        white-space: nowrap;
        text-overflow: ellipsis;
    }

    .vue-js-switch {
        margin: 2px 0 0 10px;
    }
</style>

<style>
    #specialDropdown button {
        padding: 0!important;
        border: none!important;
        width: 22.5px!important;
        height: 28px!important;
    }

    #specialDropdown {
        width: 22.5px!important;
        height: 28px!important;
        position: relative!important;
        bottom: 4px;
    }

    .dt-entries {
        margin-left: auto;
        margin-right: 1.25rem;
        color: #337ab7;
    }
</style>


// WEBPACK FOOTER //
// src/components/global/tables/datatables/DataTable.vue